import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { 
  Box,
  Button,
  Flex,
  useToast,
  Heading,
  Text
} from '@chakra-ui/react';
import SignaturePad from 'signature_pad';
import { cqAPI, HOST } from "../../lib/Auth";
import * as pdfjsLib from 'pdfjs-dist/webpack';
import {usePinch} from '@use-gesture/react';

const SignDocument = ({ signatureRequestId, onSuccess }) => {
  const signatureCanvasRef = useRef(null);
  const documentCanvasContainerRef = useRef(null);
  const documentFrameRef = useRef(null);
  const pageOverlaysRef = useRef({});   // pageNumber -> overlay DIV
  const pageViewportsRef = useRef({});  // pageNumber -> pdf.js viewport used to render
  const [signaturePad, setSignaturePad] = useState(null);
  const [fileType, setFileType] = useState('');
  const [content, setContent] = useState(null);
  const [isSignatureEmpty, setIsSignatureEmpty] = useState(true);

  // === NEW: initials data/state ===
  const [requiredInitials, setRequiredInitials] = useState([]); // [{id,page,x,y,w,h}]
  const [displayInitials, setDisplayInitials] = useState(null);
  const [completedInitials, setCompletedInitials] = useState(new Set());

  const toast = useToast();
  const { request_id } = useParams();
  const [signed, setSigned] = useState(false);
  const [signedSignatureUrl, setSignedSignatureUrl] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const actualRequestId = signatureRequestId || request_id;

  useEffect(() => {
    const fetchData = async () => {
      const signedUrl = await cqAPI.get(`/forms/sign?request_id=${actualRequestId}`);
      setSignedSignatureUrl(signedUrl);
      if (signedUrl.type === 'error') {
        toast({
          title: 'Error',
          description: signedUrl.msg,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        navigate('/forms');
        return;
      }
      const response = await fetch(`${HOST}${signedUrl.data}`);
      const result = await response.json();
      if (result.type === 'error') {
        if (result.msg === 'This is already signed.') {
          setSigned(true);
          return;
        }
        toast({
          title: 'Error',
          description: result.msg,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const data = result.data;
      setFileType(data.fileType);
      setContent(Uint8Array.from(data.content.data));

      setRequiredInitials(Array.isArray(data.requiredInitials) ? data.requiredInitials : []);
      setDisplayInitials(data.initials || null);
    };

    fetchData();
  }, [actualRequestId, toast, navigate]);

  const initializeSignaturePad = () => {
    if (signatureCanvasRef.current) {
      const canvas = signatureCanvasRef.current;
      const pad = new SignaturePad(canvas);
      setSignaturePad(pad);

      // Resize the canvas to match the container
      const resizeCanvas = () => {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        canvas.getContext('2d').scale(ratio, ratio);
        pad.clear(); // otherwise isEmpty() might return incorrect value
      };

      window.addEventListener('resize', resizeCanvas);
      resizeCanvas(); // Initialize on mount

      // Add event listeners to track signature pad changes
      pad.addEventListener('beginStroke', () => {
        setIsSignatureEmpty(false);
      });

      return () => window.removeEventListener('resize', resizeCanvas);
    }
  };

  // === helper: map PDF rect -> CSS pixels on top of rendered canvas ===
  // rect.x/y/w/h are in PDF user space units.
  // pdfViewport.width/height are device pixels after scaling (vp.scale).
  // canvasClientWidth/Height are CSS pixels (what the overlay uses).
  const toCssRect = (rect, pdfViewport, canvasClientWidth, canvasClientHeight) => {
    // device pixels from PDF units
    const devScale = pdfViewport.scale; // pixels per PDF unit
    const devX = rect.x * devScale;
    const devW = rect.w * devScale;
    const devYBottom = rect.y * devScale;
    const devH = rect.h * devScale;
    const devTop = pdfViewport.height - (devYBottom + devH);

    // convert device -> CSS using current canvas CSS size
    const cssScaleX = canvasClientWidth / pdfViewport.width;
    const cssScaleY = canvasClientHeight / pdfViewport.height;

    return {
      left: devX * cssScaleX,
      top:  devTop * cssScaleY,
      width: devW * cssScaleX,
      height: devH * cssScaleY
    };
  };

  // === build or rebuild overlays for initials (per page) ===
  const buildInitialOverlays = () => {
    const container = documentCanvasContainerRef.current;
    if (!container || !requiredInitials.length) return;

    const canvases = Array.from(container.querySelectorAll('canvas'));
    if (!canvases.length) return;

    // Ensure one overlay per page
    canvases.forEach((canvas, idx) => {
      const pageNum = idx + 1;
      let overlay = pageOverlaysRef.current[pageNum];
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.style.position = 'absolute';
        overlay.style.pointerEvents = 'none';  // overlay itself
        overlay.style.zIndex = 2;
        pageOverlaysRef.current[pageNum] = overlay;
        container.appendChild(overlay);
      }
      // position & size overlay to match canvas (CSS pixels)
      const rect = canvas.getBoundingClientRect();
      const parentRect = container.getBoundingClientRect();
      overlay.style.left = `${canvas.offsetLeft}px`;
      overlay.style.top  = `${canvas.offsetTop}px`;
      overlay.style.width = `${canvas.clientWidth}px`;
      overlay.style.height = `${canvas.clientHeight}px`;

      // clear previous boxes
      overlay.innerHTML = '';
    });

    // Add boxes
    requiredInitials.forEach(r => {
      const canvas = canvases[r.page - 1];
      const overlay = pageOverlaysRef.current[r.page];
      const vp = pageViewportsRef.current[r.page];
      if (!canvas || !overlay || !vp) return;

      const cssRect = toCssRect(r, vp, canvas.clientWidth, canvas.clientHeight);
      const done = completedInitials.has(r.id);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.initialId = r.id;
      btn.style.position = 'absolute';
      btn.style.left = `${cssRect.left}px`;
      btn.style.top = `${cssRect.top}px`;
      btn.style.width = `${cssRect.width}px`;
      btn.style.height = `${cssRect.height}px`;
      btn.style.border = done ? '2px solid rgba(0,150,0,0.9)' : '2px solid rgba(0,0,0,0.6)';
      btn.style.background = done ? 'rgba(144, 238, 144, 0.35)' : 'rgba(255, 215, 0, 0.35)';
      btn.style.borderRadius = '6px';
      btn.style.cursor = 'pointer';
      btn.style.pointerEvents = 'auto'; // clickable
      btn.style.zIndex = 3;

      const chip = document.createElement('span');
      chip.textContent = done ? (displayInitials || '') : '';
      chip.style.position = 'absolute';
      chip.style.right = '4px';
      chip.style.bottom = '2px';
      chip.style.fontWeight = '800';
      chip.style.fontSize = '12px';
      chip.style.color = done ? '#0b6d0b' : 'transparent';
      chip.style.pointerEvents = 'none';
      btn.appendChild(chip);

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        setCompletedInitials(prev => {
          const next = new Set(prev);
          if (next.has(r.id)) {
            next.delete(r.id);
            chip.textContent = '';
            chip.style.color = 'transparent';
            btn.style.background = 'rgba(255, 215, 0, 0.35)';
            btn.style.border = '2px solid rgba(0,0,0,0.6)';
          } else {
            next.add(r.id);
            chip.textContent = displayInitials || '';
            chip.style.color = '#0b6d0b';
            btn.style.background = 'rgba(144, 238, 144, 0.35)';
            btn.style.border = '2px solid rgba(0,150,0,0.9)';
          }
          return next;
        });
      });

      overlay.appendChild(btn);
    });

    // Auto-scroll to the first missing
    const firstMissing = requiredInitials.find(r => !completedInitials.has(r.id));
    if (firstMissing) {
      const canvas = canvases[firstMissing.page - 1];
      if (canvas) canvas.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  useEffect(() => {
    if (content && fileType === 'pdf') {
      const loadPDF = async () => {
        try {
          const loadingTask = pdfjsLib.getDocument({ data: content });
          const pdf = await loadingTask.promise;
          const canvasContainer = documentCanvasContainerRef.current;

          // Clear any existing canvases
          canvasContainer.innerHTML = '';
          pageOverlaysRef.current = {};
          pageViewportsRef.current = {};
          if (getComputedStyle(canvasContainer).position === 'static') {
            canvasContainer.style.position = 'relative';
          }

          // Get container width
          const containerWidth = canvasContainer.offsetWidth;

          for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
            const page = await pdf.getPage(pageNumber);
            const viewport = page.getViewport({ scale: 1 });

            // Calculate scale based on container width
            const scale = containerWidth / viewport.width;
            const usedViewport = page.getViewport({ scale }); // logical viewport (no DPR baked in)
            pageViewportsRef.current[pageNumber] = usedViewport;

            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            // 🔹 Render at devicePixelRatio resolution for sharpness
            const dpr = Math.max(window.devicePixelRatio || 1, 1);

            // Backing store in device pixels
            canvas.width  = Math.floor(usedViewport.width  * dpr);
            canvas.height = Math.floor(usedViewport.height * dpr);

            // CSS size stays responsive (logical pixels)
            canvas.style.width  = '100%';
            canvas.style.height = 'auto';

            // Optional: reduce smoothing for crisper text
            if (context) {
              context.imageSmoothingEnabled = false;
            }

            canvasContainer.appendChild(canvas);

            // Let pdf.js apply DPR via transform
            await page.render({
              canvasContext: context,
              viewport: usedViewport,
              transform: [dpr, 0, 0, dpr, 0, 0],
            }).promise;
          }
          

          // Build overlays once pages are rendered
          buildInitialOverlays();

          initializeSignaturePad();
        } catch (error) {
          console.error('Error loading PDF:', error);
        }
      };

      loadPDF();
    } else if (content && fileType === 'html') {
      const documentFrame = documentFrameRef.current;
      documentFrame.src = `data:text/html;base64,${btoa(
        String.fromCharCode.apply(null, content)
      )}`;
      initializeSignaturePad();
    }
  }, [content, fileType]);

  // Rebuild overlay positions when initials state/viewport changes
  useEffect(() => {
    if (!content || fileType !== 'pdf') return;
    buildInitialOverlays();

  }, [requiredInitials, completedInitials, displayInitials]);

  const handleClear = () => {
    if (signaturePad) {
      signaturePad.clear();
      setIsSignatureEmpty(true);
    }
  };

  const handleSave = async () => {
    // Block save until all initials acknowledged
    const needInitials = requiredInitials.length > 0;
    const allInitialed = completedInitials.size === requiredInitials.length;
    if (needInitials && !allInitialed) {
      toast({
        title: 'Please acknowledge all highlighted boxes before signing.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true); // Start loading
    try {
      const signatureData = signaturePad.toDataURL();

      const response = await fetch(`${HOST}${signedSignatureUrl.data}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          signature: signatureData.split(',')[1],
          ...(requiredInitials.length ? {
            initials: requiredInitials.map(r => ({ id: r.id, text: displayInitials || '' }))
          } : {})
        })
      });

      setIsSignatureEmpty(true);

      const data = await response.json();
      if (data.type === 'ok') {
        setSigned(true);
        onSuccess && onSuccess(); // Call the onSuccess callback
      } else if (data.msg) {
        toast({
          title: 'Error',
          description: data.msg,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error in handleSave:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false); // Ensure loading state is reset
    }
  };

  // Keep your existing pinch zoom, but mirror overlay widths/positions to canvas
  const pinchHandler = usePinch(state => {
    const [scale] = state.offset;
    const canvasContainer = documentCanvasContainerRef.current;
    const childNodes = canvasContainer.childNodes;
    for (let i = 0; i < childNodes.length; i++) {
      const node = childNodes.item(i);
      if (node.tagName === 'CANVAS') {
        node.style.width = `${Math.max(100 * scale, 100)}%`;

        // sync overlay after this canvas
        const pageIndex = Array.from(canvasContainer.querySelectorAll('canvas')).indexOf(node) + 1;
        const overlay = pageOverlaysRef.current[pageIndex];
        if (overlay) {
          overlay.style.left = `${node.offsetLeft}px`;
          overlay.style.top  = `${node.offsetTop}px`;
          overlay.style.width = `${node.clientWidth}px`;
          overlay.style.height = `${node.clientHeight}px`;
        }
      }
    }
    // rebuild to re-map positions under new CSS sizes
    buildInitialOverlays();
  });

  if (signed) {
    return (
      <Box overflowY="auto" maxHeight="100vh" width="100%" position="relative">
        <Flex
          justifyContent="center"
          alignItems="center"
          height="100vh"
          bg="gray.100"
        >
          <Box
            p={8}
            bg="white"
            boxShadow="md"
            borderRadius="md"
            textAlign="center"
          >
            <Heading color="black" as="h2" size="lg" mb={4}>
              Thank you
            </Heading>
            <Text color="black">This form is now signed.</Text>
          </Box>
        </Flex>
      </Box>
    );
  }

  const needInitials = requiredInitials.length > 0;
  const allInitialed = completedInitials.size === requiredInitials.length;

  return (
    <Box p={4} overflowY="auto" width="100%" position="relative">
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Heading as="h1" size="lg">
          Sign Document
        </Heading>
      </Flex>
      <Box {...pinchHandler()}
        ref={documentCanvasContainerRef}
        style={{ border: '1px solid #000', position: 'relative' }}
      >
        {fileType === 'html' && (
          <iframe
            ref={documentFrameRef}
            style={{ width: '100%', height: '500px' }}
          />
        )}
      </Box>
      {fileType && (
        <Box mt={4} position="relative">
          <Text mb={2}>Signature:</Text>

          {/* light blocker until all initials done (visual only, pad still works when unblocked) */}
          {needInitials && !allInitialed && (
            <Box
              position="absolute"
              top="26px"
              left={0}
              right={0}
              height="206px"
              bg="rgba(0,0,0,0.25)"
              zIndex={2}
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
              pointerEvents="auto"
            >
              <Text color="white" fontWeight="bold">
                Please tap all highlighted boxes above before signing.
              </Text>
            </Box>
          )}

          <canvas
            ref={signatureCanvasRef}
            style={{
              border: '1px solid #000',
              width: '100%',
              height: '200px',
              backgroundColor: 'white',
            }}
          />
          <Flex mt={2} mb={100} justifyContent="space-between">
            <Button onClick={handleClear} colorScheme="red">
              Clear
            </Button>
            <Button
              onClick={handleSave}
              colorScheme="blue"
              isDisabled={isSignatureEmpty || isLoading || (needInitials && !allInitialed)}
              isLoading={isLoading}
            >
              Save
            </Button>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default SignDocument;
